import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Alert02Icon,
  LinkSquare02Icon,
  Tick02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import type {
  PropertyMonitoringStatus,
  UnitMonitoringStatus
} from "api/propertyShield";
import { cx } from "shared/utils";

const FIELD_LABELS: Record<
  UnitMonitoringStatus["missing_fields"][number],
  string
> = {
  street: "street",
  city: "city",
  state: "state",
  zip_code: "ZIP code"
};

const joinFields = (fields: string[]) =>
  fields.length > 1
    ? `${fields.slice(0, -1).join(", ")} and ${fields[fields.length - 1]}`
    : fields[0];

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

const issueText = (unit: UnitMonitoringStatus) => {
  if (unit.issue === "not_on_map")
    return (
      <>
        We can&apos;t find{" "}
        <span className="text-content-1000">{unit.address}</span> on the map
      </>
    );

  const fields = unit.missing_fields.map((field) => FIELD_LABELS[field]);
  return `${capitalize(joinFields(fields))} ${fields.length > 1 ? "are" : "is"} missing`;
};

const fixLabel = (unit: UnitMonitoringStatus) => {
  if (unit.issue === "not_on_map") return "Check address";
  return unit.missing_fields.length === 1 &&
    unit.missing_fields[0] === "zip_code"
    ? "Add ZIP code"
    : "Complete address";
};

const unitName = (unit: UnitMonitoringStatus) => unit.name || `Unit ${unit.id}`;

const Dot = ({ tone }: { tone: "ok" | "warn" | "idle" }) => (
  <span
    className={cx(
      "size-2 shrink-0 rounded-full",
      tone === "ok" && "bg-success-500",
      tone === "warn" && "bg-warning-500",
      tone === "idle" && "border-[1.5px] border-content-200"
    )}
  />
);

const IssueList = ({
  propertyId,
  units
}: {
  propertyId: number;
  units: UnitMonitoringStatus[];
}) => (
  <div className="mt-3.5 overflow-hidden rounded-lg border border-alpha-40">
    <div className="flex items-start gap-2.5 border-b border-alpha-40 bg-warning-50 px-4 py-3.5">
      <HugeiconsIcon
        icon={Alert02Icon}
        size={16}
        className="mt-px shrink-0 text-warning-700"
      />
      <div className="text-[13px] leading-[18px]">
        <p className="font-bold text-content-1000">
          {units.length === 1
            ? "1 unit can't be monitored yet"
            : `${units.length} units can't be monitored yet`}
        </p>
        <p className="text-content-400">
          Property Shield needs a complete address it can find on a map.
        </p>
      </div>
    </div>
    <ul>
      {units.map((unit) => (
        <li
          key={unit.id}
          className="flex min-h-[52px] items-center gap-3 border-b border-alpha-40 px-4 py-2 text-[13px] leading-[18px] last:border-b-0"
        >
          <span className="w-16 shrink-0 font-bold">{unitName(unit)}</span>
          <span className="min-w-0 flex-1 text-content-400">
            {issueText(unit)}
          </span>
          <a
            href={`/properties/${propertyId}/units/${unit.id}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${fixLabel(unit)} in ${unitName(unit)} settings (opens in a new tab)`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-2 py-1.5 font-semibold text-primary-500 hover:bg-primary-20 hover:text-primary-600"
          >
            {fixLabel(unit)}
            <HugeiconsIcon icon={LinkSquare02Icon} size={14} />
          </a>
        </li>
      ))}
    </ul>
  </div>
);

/** Per-unit readiness under the monitoring switch: which listed units Property Shield can take. */
export const MonitoringStatus = ({
  propertyId,
  status
}: {
  propertyId: number;
  status: PropertyMonitoringStatus;
}) => {
  const [showUnits, setShowUnits] = useState(false);
  const blocked = status.units.filter((unit) => unit.issue);

  if (status.listed_count === 0)
    return (
      <div>
        <div className="flex min-h-6 flex-wrap items-center gap-2.5">
          <Dot tone="idle" />
          <span className="text-sm font-semibold">No listed units yet</span>
        </div>
        <p className="ml-[18px] mt-0.5 text-[13px] leading-[18px] text-content-400">
          Monitoring starts on its own once a unit is listed.
        </p>
      </div>
    );

  if (blocked.length === 0)
    return (
      <div>
        <div className="flex min-h-6 flex-wrap items-center gap-2.5">
          <Dot tone="ok" />
          <span className="text-sm font-semibold">
            {status.listed_count === 1
              ? "Monitoring 1 listed unit"
              : `Monitoring all ${status.listed_count} listed units`}
          </span>
          <button
            type="button"
            aria-expanded={showUnits}
            onClick={() => setShowUnits((open) => !open)}
            className="text-[13px] font-semibold text-content-400 hover:text-content-700"
          >
            {showUnits ? "Hide" : "Show"} units
          </button>
        </div>
        <Show if={showUnits}>
          <ul className="mt-3 rounded-lg border border-alpha-40">
            {status.units.map((unit) => (
              <li
                key={unit.id}
                className="flex min-h-11 items-center gap-3 border-b border-alpha-40 px-4 text-[13px] last:border-b-0"
              >
                <span className="w-16 shrink-0 font-bold">
                  {unitName(unit)}
                </span>
                <span className="min-w-0 flex-1 truncate text-content-400">
                  {unit.address}
                </span>
                <HugeiconsIcon
                  icon={Tick02Icon}
                  size={16}
                  className="shrink-0 text-success-600"
                />
              </li>
            ))}
          </ul>
        </Show>
      </div>
    );

  return (
    <div>
      <div className="flex min-h-6 items-center gap-2.5">
        <Dot tone="warn" />
        <span className="text-sm font-semibold">
          {status.monitored_count > 0
            ? `Monitoring ${status.monitored_count} of ${status.listed_count} listed units`
            : "Not monitoring any units yet"}
        </span>
      </div>
      <IssueList propertyId={propertyId} units={blocked} />
    </div>
  );
};
